import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { drawingsAPI } from '../services/api';
import { useProfile } from '../context/ProfileContext';

const COLORS = ['#2C3E50','#E74C3C','#E67E22','#F1C40F','#27AE60','#3498DB','#9B59B6','#1ABC9C','#FF69B4','#FFFFFF'];
const SIZES = [4, 8, 14, 22];
const TOOLS = [
  { id: 'pen', label: '✏️', title: 'Pen' },
  { id: 'eraser', label: '🧹', title: 'Eraser' },
  { id: 'fill', label: '🪣', title: 'Fill' }
];

export default function DrawingScreen() {
  const navigate = useNavigate();
  const { profile, refreshProfile } = useProfile();
  const canvasRef = useRef(null);
  const [color, setColor] = useState('#2C3E50');
  const [size, setSize] = useState(8);
  const [tool, setTool] = useState('pen');
  const [drawing, setDrawing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const lastPos = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDraw = useCallback((e) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    const pos = getPos(e, canvas);
    setDrawing(true);
    lastPos.current = pos;

    if (tool === 'fill') {
      // Simple flood fill (bucket)
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, canvas.width, canvas.height); // simplified fill
    }
  }, [tool, color]);

  const draw = useCallback((e) => {
    if (!drawing || tool === 'fill') return;
    e.preventDefault();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);

    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = tool === 'eraser' ? '#FFFFFF' : color;
    ctx.lineWidth = tool === 'eraser' ? size * 3 : size;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();

    lastPos.current = pos;
  }, [drawing, tool, color, size]);

  const stopDraw = useCallback(() => {
    setDrawing(false);
    lastPos.current = null;
  }, []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const saveDrawing = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      const canvas = canvasRef.current;
      const canvasData = canvas.toDataURL('image/png');

      // Create a smaller thumbnail
      const thumbCanvas = document.createElement('canvas');
      thumbCanvas.width = 200;
      thumbCanvas.height = 150;
      const thumbCtx = thumbCanvas.getContext('2d');
      thumbCtx.drawImage(canvas, 0, 0, 200, 150);
      const thumbnail = thumbCanvas.toDataURL('image/jpeg', 0.7);

      await drawingsAPI.save({
        profileId: profile.id,
        title: `Drawing ${new Date().toLocaleDateString()}`,
        canvasData,
        thumbnail,
        toolUsed: 'magic_drawing'
      });

      setSaved(true);
      refreshProfile();
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      console.error('Failed to save:', e);
      alert('Could not save drawing. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="screen" style={{ background: '#F8F9FA' }}>
      {/* Header */}
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #FF9F43, #FF6B6B)', paddingBottom: 16 }}>
        <button className="btn-back" onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.25)', color: 'white' }}>←</button>
        <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.4rem', flex: 1 }}>🎨 Magic Drawing</h1>
        <button
          className="btn btn-sm"
          onClick={saveDrawing}
          disabled={saving}
          style={{ background: 'rgba(255,255,255,0.9)', color: '#FF6B6B', fontWeight: 800, minWidth: 70 }}
        >
          {saving ? '...' : saved ? '✅' : '💾 Save'}
        </button>
      </div>

      {/* Toolbar */}
      <div style={{ padding: '10px 16px', background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {/* Tool selector */}
        <div style={{ display: 'flex', gap: 6 }}>
          {TOOLS.map(t => (
            <button
              key={t.id}
              title={t.title}
              onClick={() => setTool(t.id)}
              style={{
                width: 36, height: 36, borderRadius: 8, border: '2px solid',
                borderColor: tool === t.id ? 'var(--purple)' : 'transparent',
                background: tool === t.id ? '#F0EBFF' : 'var(--bg)',
                cursor: 'pointer', fontSize: '1.1rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Size selector */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {SIZES.map(s => (
            <button
              key={s}
              onClick={() => setSize(s)}
              style={{
                width: s + 12, height: s + 12,
                borderRadius: '50%', border: '2px solid',
                borderColor: size === s ? 'var(--purple)' : '#ddd',
                background: size === s ? color : '#ddd',
                cursor: 'pointer', padding: 0, minWidth: 16
              }}
            />
          ))}
        </div>

        {/* Clear */}
        <button
          onClick={clearCanvas}
          style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem' }}
          title="Clear canvas"
        >
          🗑️
        </button>
      </div>

      {/* Color Palette */}
      <div style={{ padding: '8px 16px', background: 'white', display: 'flex', gap: 8, overflowX: 'auto', borderBottom: '1px solid var(--border)' }}>
        {COLORS.map(c => (
          <button
            key={c}
            onClick={() => { setColor(c); setTool('pen'); }}
            style={{
              width: 32, height: 32, borderRadius: '50%',
              background: c, border: `3px solid ${color === c ? 'var(--purple)' : 'transparent'}`,
              cursor: 'pointer', flexShrink: 0,
              boxShadow: c === '#FFFFFF' ? '0 0 0 1px #ddd' : 'none'
            }}
          />
        ))}
      </div>

      {/* Canvas */}
      <div style={{ flex: 1, padding: '12px 16px', overflow: 'hidden' }}>
        <canvas
          ref={canvasRef}
          style={{
            width: '100%',
            height: 'calc(100vh - 280px)',
            borderRadius: 16,
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
            cursor: tool === 'eraser' ? 'cell' : 'crosshair',
            background: 'white',
            touchAction: 'none'
          }}
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={stopDraw}
          onMouseLeave={stopDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={stopDraw}
        />
      </div>

      {saved && (
        <div className="points-popup">
          ⭐ +5 points! Drawing saved!
        </div>
      )}
    </div>
  );
}
